import { useEffect, useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { track } from "@/lib/posthog";

/**
 * One-time marketing consent prompt for users who signed in through an OAuth
 * provider (Google). Google's consent screen can't host our own checkboxes,
 * so we ask right after they land in the app instead.
 */
const MarketingOptInPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const { data } = await supabase.auth.getUser();
      const user = data.user;
      if (!user || cancelled) return;

      const provider =
        ((user.app_metadata as { provider?: string } | null)?.provider ??
          user.identities?.[0]?.provider ??
          "") as string;
      if (provider === "email") return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("marketing_prompt_seen, marketing_opt_in")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled || !profile) return;
      if (profile.marketing_prompt_seen || profile.marketing_opt_in) return;

      setUserId(user.id);
      setVisible(true);
      track("marketing_optin_prompt_shown", { provider });
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const answer = async (optIn: boolean) => {
    if (!userId || saving) return;
    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({
        marketing_opt_in: optIn,
        marketing_opt_in_at: optIn ? new Date().toISOString() : null,
        marketing_prompt_seen: true,
      })
      .eq("user_id", userId);
    setSaving(false);

    if (error) {
      toast({
        title: "Couldn't save your preference",
        description: error.message,
        variant: "destructive",
      });
      return;
    }

    track("marketing_optin_answered", { opted_in: optIn });
    setVisible(false);
    if (optIn) {
      toast({ title: "You're subscribed", description: "You can unsubscribe from any email." });
    }
  };

  if (!visible) return null;

  return (
    <div className="mb-6 rounded-2xl border border-border bg-muted/50 p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 shrink-0 rounded-full bg-background flex items-center justify-center">
          <Mail className="h-4 w-4 text-foreground" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground">Want product news and offers?</p>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Get new features, design tips, and special offers by email. You can unsubscribe at any time.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" className="rounded-full" disabled={saving} onClick={() => answer(true)}>
              Yes, keep me posted
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-full"
              disabled={saving}
              onClick={() => answer(false)}
            >
              No thanks
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketingOptInPrompt;
